import { type Ref, type MaybeRefOrGetter, computed, ref, toValue } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useEditStore } from "@/composables/edit-manager";
import { useDialog } from "primevue/usedialog";
import { useToast } from "primevue/usetoast";
import type { DynamicDialogInstance } from "primevue/dynamicdialogoptions";
import { type Role } from "@/composables/role-manager";
import { type BpmnArtefact } from "@/lib/artefacts";
import { useProcessStore } from "@/composables/process-manager";
import { generateBpmn } from "@/composables/bpmn-manager/helpers";
import DefinitionGeneratorForm from "@/components/Process/DefinitionGeneratorForm.vue";

export const useGenerateBpmnDialog = ({
    roles,
    artefact,
}: {
    roles: MaybeRefOrGetter<Role[] | undefined>;
    artefact: Ref<BpmnArtefact | undefined>;
}) => {
    const route = useRoute();
    const router = useRouter();
    const toast = useToast();
    const dialog = useDialog();
    const dialogRef = ref<DynamicDialogInstance & { visible?: boolean }>();
    const isVisible = computed(() => Boolean(dialogRef.value?.visible));
    const isGenerating = ref(false);
    const { generateDefinition } = useProcessStore();
    const { startEditing } = useEditStore();

    /**
     * Generate the process definition and set it to the model.value.
     */
    const generateProcessDefinition = async (description: string) => {
        if (!description || !toValue(artefact)) {
            return;
        }

        isGenerating.value = true;
        const functionCalls = await generateDefinition(description);
        if (!functionCalls?.length) {
            toast.add({
                severity: "error",
                summary: "Unable to properly disect into steps.",
            });
            isGenerating.value = false;
            return;
        }

        // Navigate to the definition and start editing.
        await router.push({
            name: "processDefinitionPage",
            params: route.params,
        });
        startEditing();

        try {
            const xml = await generateBpmn(toValue(roles) ?? [], functionCalls);
            if (!xml) {
                throw Error("No valid BPMN created from given steps.");
            }
            toValue(artefact)?.updateContent(xml);
        } catch (error) {
            toast.add({
                severity: "error",
                summary: `Could not generate BPMN: ${error}`,
            });
            console.error(error);
        } finally {
            isGenerating.value = false;
        }
    };

    const open = () => {
        dialog.open(DefinitionGeneratorForm, {
            props: {
                header: "Generate a first draft",
                style: {
                    maxWidth: "600px",
                    width: "80%",
                },
                modal: true,
                maximizable: false,
                draggable: false,
            },
            data: {
                generateCallback: generateProcessDefinition,
                getIsGenerating: () => isGenerating.value,
            },
        });
    };

    return { open, isVisible, isGenerating };
};
